import { IsInterface, PartialTypeGuard } from "generic-type-guard";

import { Activity } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Attachment } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { ValidationInfo } from "../types/validation";

export type TypeGuard<T> = (v: unknown) => v is T;

export function isAnd<T, U>(spec1: TypeGuard<T>, spec2: TypeGuard<U>): TypeGuard<T & U> {
	return (v): v is T & U => spec1(v) && spec2(v);
}

export function isOr<T, U>(spec1: TypeGuard<T>, spec2: TypeGuard<U>): TypeGuard<T | U> {
	return (v): v is T | U => spec1(v) || spec2(v);
}

export function isNot<T, U>(spec: TypeGuard<T>): TypeGuard<U> {
	return (v): v is U => !spec(v);
}

export function isObjectOf<T, K extends keyof T = keyof T>(spec: Record<K, TypeGuard<T[K]>>): TypeGuard<T> {
	return (obj): obj is T => {
		if (!isObject(obj)) {
			return false;
		}

		for (const [key, test] of Object.entries<TypeGuard<unknown>>(spec)) {
			if (!test(obj[key])) {
				return false;
			}
		}

		return true;
	};
}

export function isOptionalOf<T>(spec: TypeGuard<T>): TypeGuard<T | undefined> {
	return (v): v is T | undefined => isUndefined(v) || spec(v);
}

export function isArrayOf<T>(spec: TypeGuard<T>): TypeGuard<T[]> {
	return (v): v is T[] => isArray(v) && v.every(spec);
}

export function isString(v: unknown): v is string {
	return typeof v === "string";
}

export function isBoolean(v: unknown): v is boolean {
	return typeof v === "boolean";
}

export function isNumber(v: unknown): v is number {
	return typeof v === "number";
}

export function isBigInt(v: unknown): v is bigint {
	return typeof v === "bigint";
}

export function isSymbol(v: unknown): v is symbol {
	return typeof v === "symbol";
}

export function isUndefined(v: unknown): v is undefined {
	return typeof v === "undefined";
}

export function isNull(v: unknown): v is null {
	return typeof v === "object" && v === null;
}

export function isObject(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null;
}

export function isArray(v: unknown): v is unknown[] {
	return Array.isArray(v);
}

export function isAttachment(v: unknown): v is Attachment {
	return isObject(v) && Attachment.isInstance(v);
}

export function isDate(object: unknown): object is Date {
	return isObject(object) && object instanceof Date;
}

export function isValidationInfo(object: unknown): object is ValidationInfo {
	return isObject(object) && "requiredFields" in object && isArrayOf(isString)(object.requiredFields);
}

/**
 * Internal A12
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

/**
 * Internal A12
 */
export function isDocument(value: unknown): value is Activity.Data.Document {
	return isRecord(value) && typeof value.id === "string" && typeof value.modelId === "string";
}

export function isMap<T>(ptt: PartialTypeGuard<unknown, T>): TypeGuard<Record<string, T>> {
	return new IsInterface().withStringIndexSignature(ptt, false).get();
}

export function isNonNullable<T>(value: T): value is NonNullable<T> {
	return value !== null && value !== undefined;
}

export function isEnumConfig(obj: unknown): obj is { value: string } {
	return isRecord(obj) && typeof obj.value === "string";
}
